import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Node as NodeTreeInterface } from 'react-checkbox-tree';

import { FoldPanel, Modal, SliderCheckbox } from '@components/common';
import PresetCcForm from '@components/routes/CmModule/ConfigurationComparison/ComparisonOptions/PresetCCForm';
import {
	TOnSubmit,
} from '@components/routes/CmModule/ConfigurationComparison/ComparisonOptions/PresetCCForm/PresetCCForm';
import CheckboxTree from '@components/routes/CmModule/ConfigurationComparison/MOTree/CheckboxTree';
import MoTreeButtons from '@components/routes/CmModule/ConfigurationComparison/MOTree/MoTreeButtons';

import { getCheckedIds, getPaths, prepareTree } from '@utils/treeFormatter/treeFormatter';

import { ICategoryPath, IMoTreeInitial, ITreePreset } from '@interfaces/components/tree';
import { ISubmittedParamsProps } from '@components/routes/CmModule/ConfigurationComparison/interfaces';

import MOTreeMavenir from '../MOTreeMavenir';

import styles from './MOTree.module.scss';

export interface IMoTreeProps {
	isOpenMOTree: boolean;
	toggleMOTree: () => void;
	moTree?: IMoTreeInitial;
	isDifferenceOnly: boolean;
	setIsDifferenceOnly: Dispatch<SetStateAction<boolean>>;
	handleSubmitPath: (path?: Array<ICategoryPath>) => void;
	submittedParams?: ISubmittedParamsProps;
	runProgress: () => void;
	handleSavePreset: (value: {
		categoryPaths: Array<ICategoryPath>;
		name: string;
	}) => void;
	treeId?: string;
	savedPreset?: ITreePreset;
	isMavenir?: boolean;
	isDisabledSubmit?: boolean;
}

const MoTree = ({
	isOpenMOTree,
	toggleMOTree,
	moTree,
	isDifferenceOnly,
	setIsDifferenceOnly,
	submittedParams,
	runProgress,
	handleSubmitPath,
	handleSavePreset,
	treeId,
	savedPreset,
	isMavenir,
	isDisabledSubmit,
}: IMoTreeProps) => {
	const [checked, setChecked] = useState<Array<string>>([]);
	const [expanded, setExpanded] = useState<Array<string>>([]);
	const [isOpenPresetForm, setIsOpenPresetForm] = useState<boolean>(false);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);

	const tree = useMemo(() => prepareTree(savedPreset?.moTreeOutDto || moTree), [savedPreset, moTree]);
	const isVisibleTree = (tree) || !!savedPreset;

	useEffect(() => {
		clearAll();
		setExpanded([]);
	}, [moTree, savedPreset]);

	useEffect(() => {
		if (!savedPreset?.moTreeOutDto || !tree) return;

		clearAll();
		setExpanded([]);

		const checkedValues = getCheckedIds({
			tree,
			paths: savedPreset.categoryPaths,
		});

		setChecked(checkedValues);
		setIsDifferenceOnly(savedPreset.onlyChanged);
	}, [
		savedPreset?.categoryPaths,
		savedPreset?.moTreeOutDto,
		savedPreset?.onlyChanged,
		setIsDifferenceOnly,
		tree,
	]);

	const handleSubmitMavenir = (value: Array<ICategoryPath>) => {
		if (!tree) return;

		if (!isMavenir && (submittedParams || savedPreset) || isMavenir) {
			const path = getPaths(tree, checked);

			if (value || path.length) {
				handleSubmitPath(value ? value : path);
				runProgress();
			}
		}
	};

	const handleSubmit = () => {
		if (!tree) return;

		if (submittedParams || savedPreset) {
			const path = getPaths(tree, checked);

			handleSubmitPath(path);
			runProgress();
		}
	};

	const clearAll = () => setChecked([]);

	const onChangeIsDifferenceOnly = () => setIsDifferenceOnly(prevState => !prevState);

	const openPresetForm = () => setIsOpenPresetForm(true);

	const closePresetForm = () => setIsOpenPresetForm(false);

	const savePresetHandler = ({ name }: { name: string }) => {
		if (!tree || !submittedParams || !treeId) return;

		const path = getPaths(tree, checked);

		handleSavePreset({
			categoryPaths: path,
			name,
		});
		closePresetForm();
	};

	return (
		<>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isOpenPresetForm}
				onClose={closePresetForm}
				title='Save as preset'
			>
				<PresetCcForm
					onClose={closePresetForm}
					onSubmit={savePresetHandler as TOnSubmit}
					setIsDirtyFormModal={setIsDirtyFormModal}
				/>
			</Modal>
			<FoldPanel
				buttonsComponent={
					isVisibleTree && !isMavenir
						? <MoTreeButtons
							checked={!!checked.length}
							clearAll={clearAll}
							handleSubmit={handleSubmit}
							isMavenir={isMavenir}
							openPresetForm={openPresetForm}
							savedPreset={savedPreset}
						/>
						: undefined
				}
				classNameBody={styles.moTreeBody}
				isOpen={isOpenMOTree}
				title='MO Tree'
				togglePanel={toggleMOTree}
				tooltip='MO Tree'
				width='400px'
			>
				{isVisibleTree
					? <>
						<div className={styles.sliderWrapper}>
							<SliderCheckbox
								checked={isDifferenceOnly}
								id='isDifferenceOnly'
								isLabelAfter
								label='Show difference only'
								onChange={onChangeIsDifferenceOnly}
							/>
						</div>
						{
							!isMavenir
								? (
									<CheckboxTree
										checked={checked}
										expanded={expanded}
										onCheck={setChecked}
										onExpand={setExpanded}
										tree={[tree as NodeTreeInterface]}
									/>
								)
								: (
									<MOTreeMavenir
										handleSubmit={handleSubmitMavenir}
										isDisabledSubmit={isDisabledSubmit}
										moTree={moTree}
										treeId={treeId}
									/>
								)

						}
					</>
					: <div className={styles.stubText}>
						Select configuration parameters to see the MO Tree
					</div>
				}
			</FoldPanel>
		</>
	);
};

export default MoTree;
